/**
 * Responsibility: hold the WebSocket to the hub open and surface stock events.
 *
 * Reconnects with exponential backoff and jitter, capped at 60 seconds, per
 * PLAN.md section 4. Never a tight reconnect loop: a GUI hammering the hub
 * during an outage makes the outage worse and buries the real error.
 *
 * The `connected` flag is deliberately exposed. A GUI that has silently lost
 * its socket looks exactly like a quiet system, and the operator needs to be
 * able to tell those apart.
 */
import { idToken } from '../auth/firebase';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { StockEvent } from '../api/client';

const MAX_EVENTS = 200;
const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 60_000;

type Envelope = { type: string; payload: unknown };

function socketUrl(): string {
  const base = import.meta.env.VITE_HUB_URL;
  if (base) {
    // The hub is on another host once the GUI moves to Firebase Hosting.
    return `${base.replace(/^http/, 'ws')}/ws/gui`;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws/gui`;
}

export type LiveEvents = {
  events: StockEvent[];
  connected: boolean;
  latest: StockEvent | null;
  clear: () => void;
};

export function useLiveEvents(onEvent?: (event: StockEvent) => void): LiveEvents {
  const [events, setEvents] = useState<StockEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const attemptRef = useRef(0);
  const socketRef = useRef<WebSocket | null>(null);
  const timerRef = useRef<number | null>(null);
  const closedRef = useRef(false);
  // Kept in a ref so a changing callback identity does not tear down the
  // socket, which would reconnect on every render of the parent.
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  const clear = useCallback(() => setEvents([]), []);

  useEffect(() => {
    closedRef.current = false;

    const connect = () => {
      if (closedRef.current) return;
      const socket = new WebSocket(socketUrl());
      socketRef.current = socket;

      socket.onopen = () => {
        // The hub wants the token as the first frame. A browser cannot set an
        // Authorization header on a WebSocket, and putting a live credential
        // in the query string would write it into every access log between
        // here and there. Nothing is sent to us until this verifies, and the
        // hub closes the socket with 1008 if it does not.
        void idToken().then((token) => {
          if (socket.readyState !== WebSocket.OPEN) return;
          socket.send(token ? `Bearer ${token}` : '');
          attemptRef.current = 0;
          setConnected(true);
        });
      };

      socket.onmessage = (message) => {
        let envelope: Envelope;
        try {
          envelope = JSON.parse(message.data as string) as Envelope;
        } catch {
          // A malformed frame must not take down a healthy connection.
          return;
        }
        if (envelope.type !== 'stock_event') return;
        const event = envelope.payload as StockEvent;
        setEvents((current) => [event, ...current].slice(0, MAX_EVENTS));
        handlerRef.current?.(event);
      };

      socket.onclose = () => {
        setConnected(false);
        if (closedRef.current) return;
        const attempt = (attemptRef.current += 1);
        const backoff = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS);
        // Jitter so several open tabs do not reconnect in lockstep.
        const delay = backoff * (0.6 + Math.random() * 0.8);
        timerRef.current = window.setTimeout(connect, delay);
      };

      socket.onerror = () => socket.close();
    };

    connect();

    return () => {
      closedRef.current = true;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      socketRef.current?.close();
    };
  }, []);

  return { events, connected, latest: events[0] ?? null, clear };
}
